import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Building2, Baby, Users, ChevronDown, ChevronUp, User, ExternalLink,
  Clock, LogIn, LogOut, AlertTriangle, MapPin, CornerUpLeft, UserCheck
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

import API from '@/config/api';
// API imported from config;

// Common locations a child might go
const AWAY_LOCATIONS = [
  { value: 'Bathroom', label: '🚻 Bathroom' },
  { value: 'Nurse', label: '🏥 Nurse/Health Room' },
  { value: 'Office', label: '🏢 Office' },
  { value: 'Playground', label: '🎪 Playground' },
  { value: 'Cafeteria', label: '🍽️ Cafeteria' },
  { value: 'Library', label: '📚 Library' },
  { value: 'Gym', label: '🏃 Gym' },
  { value: 'Other Classroom', label: '🚪 Other Classroom' },
];

const ClassroomsWidget = () => {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [classrooms, setClassrooms] = useState([]);
  const [children, setChildren] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedClassroom, setExpandedClassroom] = useState(null);
  
  // Away dialog state
  const [awayDialogOpen, setAwayDialogOpen] = useState(false);
  const [selectedChildForAway, setSelectedChildForAway] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedEscort, setSelectedEscort] = useState('');

  useEffect(() => {
    fetchData();
    // Refresh data every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const [classroomsRes, childrenRes, attendanceRes, staffRes] = await Promise.all([
        axios.get(`${API}/classrooms`, getAuthHeaders()),
        axios.get(`${API}/children`, getAuthHeaders()),
        axios.get(`${API}/attendance?date=${today}`, getAuthHeaders()),
        axios.get(`${API}/staff`, getAuthHeaders())
      ]);
      setClassrooms(classroomsRes.data);
      setChildren(childrenRes.data);
      setAttendance(attendanceRes.data);
      setStaff(staffRes.data);
    } catch (error) {
      console.error('Failed to load classrooms data');
    } finally {
      setLoading(false);
    }
  };

  const getChildrenInClassroom = (classroomName) => {
    return children.filter(child => child.classroom === classroomName && child.is_checked_in === true);
  };

  const getTotalCheckedIn = () => {
    return children.filter(child => child.is_checked_in === true).length;
  };

  const getChildAttendance = (childId) => {
    return attendance.find(a => a.child_id === childId);
  };

  const formatTime = (isoString) => {
    if (!isoString) return '--:--';
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getTimeSince = (isoString) => {
    if (!isoString) return '';
    const left = new Date(isoString);
    const now = new Date();
    const diffMs = now - left;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    return `${diffHours}h ${diffMins % 60}m ago`;
  };

  const handleMarkAway = (child, e) => {
    e.stopPropagation();
    setSelectedChildForAway(child);
    setSelectedLocation('');
    setSelectedEscort('');
    setAwayDialogOpen(true);
  };

  const handleConfirmAway = async () => {
    if (!selectedChildForAway || !selectedLocation) return;
    
    // Find escort name
    const escortStaff = staff.find(s => s.id === selectedEscort);
    const escortName = escortStaff?.name || user?.name || 'Staff';
    
    try {
      await axios.post(
        `${API}/children/${selectedChildForAway.id}/away`,
        { 
          location: selectedLocation,
          escort_id: selectedEscort || null,
          escort_name: escortName
        },
        getAuthHeaders()
      );
      toast.success(`${selectedChildForAway.first_name} marked as away - ${selectedLocation}`);
      setAwayDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark child as away');
    }
  };

  const handleMarkReturned = async (child, e) => {
    e.stopPropagation();
    try {
      await axios.post(
        `${API}/children/${child.id}/returned`,
        {},
        getAuthHeaders()
      );
      toast.success(`${child.first_name} returned to classroom`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark child as returned');
    }
  };

  const getAgeFromDob = (dob) => {
    if (!dob) return 'N/A';
    const birthDate = new Date(dob);
    const today = new Date();
    const months = (today.getFullYear() - birthDate.getFullYear()) * 12 + 
                   (today.getMonth() - birthDate.getMonth());
    
    if (months < 12) return `${months}m`;
    if (months < 24) return `${Math.floor(months / 12)}y ${months % 12}m`;
    return `${Math.floor(months / 12)}y`;
  };

  const getClassroomColor = (index) => {
    const colors = [
      'from-pink-100 to-pink-200 border-pink-300',
      'from-blue-100 to-blue-200 border-blue-300',
      'from-green-100 to-green-200 border-green-300',
      'from-purple-100 to-purple-200 border-purple-300',
      'from-amber-100 to-amber-200 border-amber-300',
      'from-teal-100 to-teal-200 border-teal-300',
    ];
    return colors[index % colors.length];
  };

  const getClassroomIcon = (name) => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('infant')) return '👶';
    if (lowerName.includes('toddler')) return '🧒';
    if (lowerName.includes('preschool') || lowerName.includes('pre-k')) return '🎨';
    if (lowerName.includes('school')) return '📚';
    return '🏫';
  };

  const getCapacityStatus = (current, capacity) => {
    const percentage = (current / capacity) * 100;
    if (percentage >= 90) return { color: 'bg-red-500', text: 'text-red-600', status: 'Near Full' };
    if (percentage >= 70) return { color: 'bg-amber-500', text: 'text-amber-600', status: 'Filling Up' };
    return { color: 'bg-green-500', text: 'text-green-600', status: 'Available' };
  };

  // Get staff working today (or all active staff)
  const getAvailableEscorts = () => {
    return staff.filter(s => s.status === 'active' || !s.status);
  };

  if (loading) {
    return (
      <Card className="card-clay">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/3"></div>
            <div className="h-20 bg-muted rounded"></div>
            <div className="h-20 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalCheckedIn = getTotalCheckedIn();
  const availableEscorts = getAvailableEscorts();

  return (
    <>
      <Card className="card-clay" data-testid="classrooms-widget">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Today's Census
            </CardTitle>
            <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
              {totalCheckedIn} checked in
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[450px] pr-2">
            <div className="space-y-3">
              {classrooms.map((classroom, index) => {
                const classroomChildren = getChildrenInClassroom(classroom.name);
                const isExpanded = expandedClassroom === classroom.id;
                const capacityStatus = getCapacityStatus(classroomChildren.length, classroom.capacity);
                const spotsRemaining = classroom.capacity - classroomChildren.length;
                const capacityPercentage = Math.min((classroomChildren.length / classroom.capacity) * 100, 100);
                
                const childrenAway = classroomChildren.filter(c => c.temp_location);
                const childrenPresent = classroomChildren.filter(c => !c.temp_location);
                
                return (
                  <div 
                    key={classroom.id}
                    className={`rounded-xl border-2 overflow-hidden transition-all bg-gradient-to-r ${getClassroomColor(index)}`}
                    data-testid={`classroom-${classroom.id}`}
                  >
                    <button
                      onClick={() => setExpandedClassroom(isExpanded ? null : classroom.id)}
                      className="w-full p-4 flex items-center justify-between hover:bg-white/30 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getClassroomIcon(classroom.name)}</span>
                        <div className="text-left">
                          <h4 className="font-semibold text-sm">{classroom.name}</h4>
                          <p className="text-xs text-muted-foreground">{classroom.age_range}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className={`text-lg font-bold ${classroomChildren.length > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                            {classroomChildren.length}/{classroom.capacity}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {childrenAway.length > 0 ? (
                              <span className="text-amber-600">{childrenAway.length} away</span>
                            ) : (
                              `${spotsRemaining} spots left`
                            )}
                          </p>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                    </button>
                    
                    {isExpanded && (
                      <div className="bg-white/60 border-t">
                        {/* Capacity Tracker */}
                        <div className="px-4 py-3 border-b bg-white/40">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-gray-600">Daily Capacity</span>
                            <span className={`text-xs font-semibold ${capacityStatus.text}`}>
                              {capacityStatus.status}
                            </span>
                          </div>
                          <Progress value={capacityPercentage} className="h-2" />
                          <div className="flex justify-between mt-1">
                            <span className="text-xs text-muted-foreground">
                              {childrenPresent.length} in room {childrenAway.length > 0 && `• ${childrenAway.length} away`}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {spotsRemaining} available
                            </span>
                          </div>
                          {spotsRemaining <= 2 && spotsRemaining > 0 && (
                            <div className="flex items-center gap-1 mt-2 text-amber-600">
                              <AlertTriangle className="w-3 h-3" />
                              <span className="text-xs">Only {spotsRemaining} spot{spotsRemaining !== 1 ? 's' : ''} remaining</span>
                            </div>
                          )}
                          {spotsRemaining === 0 && (
                            <div className="flex items-center gap-1 mt-2 text-red-600">
                              <AlertTriangle className="w-3 h-3" />
                              <span className="text-xs font-medium">Classroom at full capacity</span>
                            </div>
                          )}
                        </div>

                        {/* Attendance Log */}
                        <div className="px-4 py-3">
                          <div className="flex items-center gap-2 mb-3">
                            <Clock className="w-4 h-4 text-primary" />
                            <span className="text-xs font-semibold text-gray-700">Attendance Log</span>
                          </div>
                          
                          {classroomChildren.length > 0 ? (
                            <div className="space-y-2">
                              {classroomChildren.map((child) => {
                                const childAttendance = getChildAttendance(child.id);
                                const isAway = child.temp_location;
                                
                                return (
                                  <div 
                                    key={child.id}
                                    className={`flex items-center justify-between p-3 rounded-lg shadow-sm transition-all cursor-pointer group ${
                                      isAway 
                                        ? 'bg-amber-50 border border-amber-200' 
                                        : 'bg-white hover:shadow-md hover:bg-blue-50/50'
                                    }`}
                                    data-testid={`child-${child.id}`}
                                    onClick={() => navigate(`/children?highlight=${child.id}`)}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                        isAway 
                                          ? 'bg-amber-200' 
                                          : 'bg-gradient-to-br from-primary/20 to-primary/40'
                                      }`}>
                                        {isAway ? (
                                          <MapPin className="w-5 h-5 text-amber-600" />
                                        ) : (
                                          <User className="w-5 h-5 text-primary" />
                                        )}
                                      </div>
                                      <div>
                                        <p className={`font-medium text-sm ${isAway ? 'text-amber-800' : 'group-hover:text-primary'} transition-colors`}>
                                          {child.first_name} {child.last_name}
                                        </p>
                                        {isAway ? (
                                          <div className="space-y-0.5">
                                            <div className="flex items-center gap-1 text-amber-600">
                                              <span className="text-xs font-medium">
                                                📍 {child.temp_location.location}
                                              </span>
                                              <span className="text-xs">
                                                • {getTimeSince(child.temp_location.left_at)}
                                              </span>
                                            </div>
                                            {child.temp_location.escort_name && (
                                              <div className="flex items-center gap-1 text-amber-700">
                                                <UserCheck className="w-3 h-3" />
                                                <span className="text-xs">
                                                  with {child.temp_location.escort_name}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          <p className="text-xs text-muted-foreground">
                                            Age: {getAgeFromDob(child.date_of_birth || child.dob)}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                      {isAway ? (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-7 text-xs bg-white border-green-300 text-green-600 hover:bg-green-50"
                                          onClick={(e) => handleMarkReturned(child, e)}
                                          data-testid={`return-btn-${child.id}`}
                                        >
                                          <CornerUpLeft className="w-3 h-3 mr-1" />
                                          Returned
                                        </Button>
                                      ) : (
                                        <>
                                          <div className="flex items-center gap-1 text-green-600">
                                            <LogIn className="w-3 h-3" />
                                            <span className="text-xs font-medium">
                                              In: {formatTime(childAttendance?.check_in_time)}
                                            </span>
                                          </div>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50 opacity-0 group-hover:opacity-100 transition-opacity"
                                            onClick={(e) => handleMarkAway(child, e)}
                                            data-testid={`away-btn-${child.id}`}
                                          >
                                            <MapPin className="w-3 h-3 mr-1" />
                                            Mark Away
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-center py-6 text-muted-foreground bg-white/50 rounded-lg">
                              <Baby className="w-10 h-10 mx-auto mb-2 opacity-40" />
                              <p className="text-sm font-medium">No children in class</p>
                              <p className="text-xs mt-1">Children will appear here when checked in</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
          
          <div className="mt-4 pt-4 border-t">
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => window.location.href = '/children'}
              data-testid="view-all-children-btn"
            >
              <Users className="w-4 h-4 mr-2" />
              View All Children
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Mark Away Dialog */}
      <Dialog open={awayDialogOpen} onOpenChange={setAwayDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <MapPin className="w-5 h-5 text-amber-500" />
              Mark Child Away
            </DialogTitle>
            <DialogDescription>
              Where is {selectedChildForAway?.first_name} going and who is escorting?
            </DialogDescription>
          </DialogHeader>
          
          {selectedChildForAway && (
            <div className="space-y-4 mt-2">
              {/* Child info */}
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="font-bold text-primary">
                    {selectedChildForAway.first_name.charAt(0)}{selectedChildForAway.last_name.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="font-medium">{selectedChildForAway.first_name} {selectedChildForAway.last_name}</p>
                  <p className="text-xs text-muted-foreground">{selectedChildForAway.classroom}</p>
                </div>
              </div>

              {/* Location selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Where are they going?</label>
                <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                  <SelectTrigger data-testid="location-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {AWAY_LOCATIONS.map((loc) => (
                      <SelectItem key={loc.value} value={loc.value}>
                        {loc.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Escort selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-muted-foreground" />
                  Who is escorting?
                </label>
                <Select value={selectedEscort} onValueChange={setSelectedEscort}>
                  <SelectTrigger data-testid="escort-select">
                    <SelectValue placeholder="Select staff member" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableEscorts.map((staffMember) => (
                      <SelectItem key={staffMember.id} value={staffMember.id}>
                        {staffMember.name} {staffMember.position ? `(${staffMember.position})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Select the staff member accompanying the child
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setAwayDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-amber-500 hover:bg-amber-600"
                  onClick={handleConfirmAway}
                  disabled={!selectedLocation || !selectedEscort}
                  data-testid="confirm-away-btn"
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Mark Away
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClassroomsWidget;
